import '@typeform/embed/build/css/widget.css'
import * as typeformEmbed from '@typeform/embed';
import { Button, Col, Row } from 'antd';
import classnames from 'classnames';
import { config as appConfig } from 'config/config';
import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import apiPaths from 'services/apiPaths';
import { QuizTypeformDetailWrapper } from 'src/components/challenge/QuizTypeformDetailWrapper';
import CustomModal from 'src/components/CustomModal';
import { openToast } from 'src/components/general/Toast';
import { IRootReducers } from 'src/reducers';
import getConfig from 'next/config';
import {
  ButtonStatusEnum,
  ChallengeTypeEnum,
  ExperienceDetailStatusEnum,
} from 'src/shared/enums';
import {
  IChallengeQuizTypeform,
  IChallengeResourceRender,
  IExperienceDetailWrapper,
  IUser,
} from 'src/shared/models';
import {
  breakpoints,
  checkUserResourcePoints,
  completeChallengeUserResource,
  dispatchCustomEvent,
  slugify,
  useWindowSize,
} from 'utils';

const { DEFAULT_LANGUAGE } = getConfig().publicRuntimeConfig;

interface OwnProps extends IChallengeResourceRender {
  config: IChallengeQuizTypeform;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const TypeformComponent: FC<{
  url: string;
  showSurvey: boolean;
  handleSubmit: () => void;
  isMobile: boolean;
  handleClose?: () => void;
  user: IUser
}> = ({ url, showSurvey, handleSubmit, isMobile, handleClose, user }) => {
  const typeformRef = useRef(null);
  const uuidUser = user.uuidUser;

  useEffect(() => {
    if (isMobile) {
      typeformEmbed.createWidget(url, {
        container: typeformRef.current,
        hideFooter: true,
        hideHeaders: true,
        onSubmit: handleSubmit,
        hidden: {
          country: `${DEFAULT_LANGUAGE.trim().split('-').pop()}`,
          email: `${user.email}`,
          uuid_user: `${uuidUser}`,
        }
      });
    } else {
      typeformEmbed.createWidget(url, {
        container: typeformRef.current,
        hideFooter: true,
        hideHeaders: true,
        onSubmit: handleSubmit,
        opacity: 0,
        hidden: {
          country: `${DEFAULT_LANGUAGE.trim().split('-').pop()}`,
          email: `${user.email}`,
          uuid_user: `${uuidUser}`,
        }
      });
    }
  }, [typeformRef]);

  return (
    <Row justify="center" align="middle">
      <div
        ref={typeformRef}
        style={
          showSurvey ? { width: '100%', height: '500px' } : { display: 'none' }
        }
      />
    </Row>
  );
};

const ChallengeQuizTypeform: FC<Props> = ({
  challenge,
  config,
  updateChallenge,
  updateUserPoints,
  user,
  userPoints,
}) => {
  const { formatMessage } = useIntl();
  const {
    title,
    buttonName,
    idTypeform,
    idResource,
    points,
    quizDescription,
    quizImage,
    quizTitle,
    quizVideo,
  } = config;
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const challengeType: number = challenge?.challengeType;

  const isExperience =
    challenge?.challengeType === ChallengeTypeEnum.EXPERIENCE && config;

  const { width } = useWindowSize();
  const isMobile = width <= breakpoints.lg;
  const url = `${appConfig.APP.TYPEFORM_URL}/${idTypeform}#user_id=${user?.uuidUser}&survey_id=${idResource}`;

  const handleOpenModal = () => {
    setShowSurvey(true);
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setError(null);
  };

  useEffect(() => {
    if (showToast && challengeType === ChallengeTypeEnum.EXPERIENCE) {
      const toastMessage = formatMessage({
        id: 'notification.quiz.success.experience',
      });
      openToast(toastMessage, 'success');
    }
    if (showToast && challengeType === ChallengeTypeEnum.CONTEST) {
      const toastMessage = formatMessage(
        { id: 'notification.quiz.success.contest' },
        {
          points: challenge?.challengePoints,
          shortDescription: challenge?.descriptionShort,
        }
      );
      openToast(toastMessage, 'success');
    }
  }, [showToast]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const err = await completeChallengeUserResource({
        dataPath: apiPaths.CHALLENGES.COMPLETE_EXPERIENCE,
        idResource: config.idResource,
        updateChallenge,
        updateUserPoints,
      });
      if (err) {
        const stringError: string = formatMessage({
          id: 'app.error.generic',
        });
        setError(stringError);
      } else if (isMobile) {
        setLoading(false);
        setModalVisible(true);
      }
      setShowToast(true);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = () => {
    isExperience &&
      dispatchCustomEvent({
        eventCategory: 'experience',
        eventAction: 'quiz',
        eventLabel: 'start',
        eventName: 'experience_quiz_start',
        eventDetail: slugify(`${challenge.challengeName}_${title}`),
      });

    handleOpenModal();
  };

  const handleFinishQuiz = () => {
    isExperience &&
      dispatchCustomEvent({
        eventCategory: 'experience',
        eventAction: 'quiz',
        eventLabel: 'complete',
        eventName: 'experience_quiz_complete',
        eventDetail: slugify(`${challenge.challengeName}_${title}`),
      });

    handleCloseModal();
  };

  const wrapperConfig: IExperienceDetailWrapper = {
    title,
    quizDescription,
    quizImage,
    quizTitle,
    quizVideo,
    points,
    status: ExperienceDetailStatusEnum.IN_PROGRESS,
    button: {
      label: buttonName,
      status: ButtonStatusEnum.ENABLED,
      performAction: handleStartQuiz,
    },
  };

  const hasUserEnoughPoints = checkUserResourcePoints(
    userPoints,
    config.points,
    challenge?.challengeType
  );

  if (config.isCompleted) {
    wrapperConfig.button = {
      label: formatMessage({
        id: 'experience.button-completed',
      }),
      performAction: null,
      status: ButtonStatusEnum.DONE,
    };
    wrapperConfig.status = ExperienceDetailStatusEnum.DONE;
  }

  if (config.isBlocked) {
    wrapperConfig.button = {
      status: ButtonStatusEnum.DISABLED,
      performAction: null,
      label: hasUserEnoughPoints ? '' : buttonName,
    };
    wrapperConfig.status = ExperienceDetailStatusEnum.DISABLED;
  }

  if (!config.isChallengeInDate) {
    wrapperConfig.button = {
      status: ButtonStatusEnum.HIDDEN,
      performAction: null,
    };
    wrapperConfig.status = ExperienceDetailStatusEnum.HIDDEN;
  }

  const modalContent = (
    <TypeformComponent
      url={url}
      showSurvey={showSurvey}
      handleSubmit={handleSubmit}
      isMobile={isMobile}
      user={user}
    />
  );

  const completedModalContent = (
    <Row justify="center" align="middle">
      <Col span={20}>
        <Row justify="center" align="middle">
          <div className="icon icon--process-check quiz-typeform-finalize-modal--icon" />
        </Row>
        <Row justify="center">
          <div className="quiz-typeform-finalize-modal--title">
            {formatMessage(
              {
                id: 'page.challenge.quiz.modal-success',
              },
              { br: <br /> }
            )}
          </div>
        </Row>
        <Row>
          <Button
            onClick={handleFinishQuiz}
            className="btn-primary--black quiz-typeform-finalize-modal--button"
            loading={loading}
            block={isMobile}
          >
            {formatMessage({
              id: 'button.fantastic',
            })}
          </Button>
        </Row>
      </Col>
    </Row>
  );

  const modalClassnames: string = classnames({
    'quiz-typeform-modal': !config.isCompleted,
    'quiz-typeform-finalize-modal': config.isCompleted,
  });

  return (
    <>
      <QuizTypeformDetailWrapper {...wrapperConfig}>
        <Fragment />
      </QuizTypeformDetailWrapper>
      {isMobile && !modalVisible ? (
        <>
          {showSurvey && (
            <TypeformComponent
              url={url}
              showSurvey={false}
              handleSubmit={handleSubmit}
              isMobile={isMobile}
              handleClose={() => setShowSurvey(false)}
              user={user}
            />
          )}
        </>
      ) : (
        <CustomModal
          {...{
            visible: modalVisible,
            loading,
            error,
            content: config.isCompleted ? completedModalContent : modalContent,
            onCancel: handleCloseModal,
            footer: null,
            showIcon: false,
            customClass: modalClassnames,
          }}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    challenge: state.app.challenge,
    user: state.auth.user,
    userPoints: state.auth.userPoints,
  };
};

export default connect(mapStateToProps)(ChallengeQuizTypeform);
